import {Dispatch, SetStateAction, useCallback, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {ActivitiesGroup} from '../../../../../../../../../backend/interfaces/activities-group';
import {ActivitiesSector} from '../../../../../../../../../backend/interfaces/activities-sector';
import {ActivityType} from '../../../../../../../../../backend/interfaces/activity-type';
import {translations} from '../../../../../../../../../i18n/translations';
import {PlannerCreateActivityStep} from './planner-create-activity-step';
import {PlannerBaseModal} from '../../planner-base-modal';
import {
  ModalBtnsOpts,
  useModalBtns,
} from '../../../../../../../../../components/modal/use-modal-btns';
import {PlannerActivityChoose} from './1-planner-activity-choose';
import {PlannerActivityForm} from './2-planner-activity-form';
import {PlannerActivityUsers} from './3-planner-activity-users';
import {AffiliatedUser} from '../../../../../../../../../backend/responses/affiliated-user';
import {EntityUserCarePlansRepository} from '../../../../../../../../../repositories/entity-user-care-plans';
import {Typography} from '@mui/material';
import {fadeInRight} from '../../../../../../../../../utils/css-animations';
import {DynamicFormManager} from '../../../../../../../../../managers/dynamic-form';
import {ConfigurationsIndex} from '../../../../../../../../../types';

export type PlannerCreateActivityModalProps = {
  open: boolean;
  activitiesSector: ActivitiesSector;
  onClose: () => void;
  onSave: (numActivitiesCreated: number) => void;
};

export function PlannerCreateActivityModal(props: PlannerCreateActivityModalProps) {
  const {t} = useTranslation();
  const [step, setStep] = useState<PlannerCreateActivityStep>('choose-activity');
  const [data, setData] = useState<CreateActivityData>();

  const actionBtns = useModalBtns(actionButtonsByStep({props, data, setData, step, setStep}));

  const handleChangeFormValues = useCallback(
    (values: Record<string, any>, activityTypeConfigurationsIndex: ConfigurationsIndex) => {
      setData((x) => ({...x, form: {...x?.form, values}, activityTypeConfigurationsIndex}));
    },
    []
  );

  const handleChangeFormIsValid = useCallback((isValid: boolean) => {
    setData((x) => ({...x, form: {...x?.form, isValid}}));
  }, []);

  return (
    <PlannerBaseModal
      open={props.open}
      onClose={props.onClose}
      title={t(translations.planner.header.add) + ' ' + props.activitiesSector.name}
      actions={actionBtns}
    >
      {(() => {
        // ------------- STEP 1 -------------
        if (step === 'choose-activity') {
          return (
            <PlannerActivityChoose
              activitiesSector={props.activitiesSector}
              activitiesGroup={data?.activitiesGroup}
              activitiesType={data?.activitiesType}
              onActivitiesGroupChange={(activitiesGroup) => {
                setData((x) => ({
                  ...x,
                  activitiesGroup,
                  selectedUsers: undefined, // reset
                  form: undefined, // reset
                  activitiesType: undefined, //reset
                }));
              }}
              onActivitiesTypeChange={(activitiesType) => {
                setData((x) => ({...x, activitiesType}));
              }}
            />
          );
        }

        // ------------- STEP 2 -------------
        if (step === 'config-activity') {
          return (
            <PlannerActivityForm
              activitiesSectorId={props.activitiesSector.id!}
              activitiesGroupId={data?.activitiesGroup?.id!}
              activitiesTypeId={data?.activitiesType?.id!}
              initialValues={data?.form?.values}
              onChangeValues={handleChangeFormValues}
              onChangeValid={handleChangeFormIsValid}
            />
          );
        }

        // ------------- STEP 3 -------------
        if (step === 'select-users' || step === 'saving' || step === 'save-error') {
          return (
            <>
              <PlannerActivityUsers
                users={data?.selectedUsers?.map((x) => x.data)}
                disabled={step === 'saving' || step === 'save-error'}
                onChange={(users) => {
                  setData((x) => ({
                    ...x,
                    selectedUsers: users.map((user) => ({data: user, status: 'pending'})),
                  }));
                }}
              />

              {step === 'save-error' && (
                <Typography
                  color="error.main"
                  sx={{
                    fontSize: '14px',
                    fontWeight: 500,
                    animation: `${fadeInRight} 0.3s`,
                    textAlign: 'center',
                  }}
                >
                  {t(translations.planner.errors.save_activity)}
                </Typography>
              )}
            </>
          );
        }
      })()}
    </PlannerBaseModal>
  );
}

// -------------------------------------------------------------------------
// -------------------------------------------------------------------------
// -------------------------------------------------------------------------

function actionButtonsByStep({
  props,
  data,
  setData,
  step,
  setStep,
}: {
  props: PlannerCreateActivityModalProps;
  data?: CreateActivityData;
  setData: SetStateCreateActivityDataAction;
  step: PlannerCreateActivityStep;
  setStep: SetStateStepAction;
}): ModalBtnsOpts {
  switch (step) {
    case 'choose-activity':
      return {
        left: {
          type: 'cancel',
          onClick: props.onClose,
        },
        right: {
          type: 'next',
          disabled: !data?.activitiesGroup || !data?.activitiesType,
          onClick: () => {
            setStep('config-activity');
          },
        },
      };

    case 'config-activity':
      return {
        left: {
          type: 'back',
          disabled: false,
          onClick: () => {
            setStep('choose-activity');
          },
        },
        right: {
          type: 'next',
          disabled: !data?.form?.isValid,
          onClick: () => {
            setStep('select-users');
          },
        },
      };

    case 'select-users':
    case 'saving':
      return {
        left: {
          type: 'back',
          disabled: step === 'saving',
          onClick: () => {
            setStep('config-activity');
          },
        },
        right: {
          type: 'save',
          loading: step === 'saving',
          disabled: (() => {
            if (step === 'saving') return true;

            if (!data?.selectedUsers) return true;

            return false;
          })(),
          onClick: async () => {
            try {
              setStep('saving');
              await saveData({props, data});
              props.onSave(data?.selectedUsers?.length ?? 0);
            } catch (error) {
              console.error(error);
              setStep('save-error');
            }
          },
        },
      };

    case 'save-error': {
      return {
        left: {
          type: 'cancel',
          onClick: props.onClose,
        },
        right: {
          type: 'save',
          onClick: async () => {
            try {
              setStep('saving');
              await saveData({props, data});
              props.onSave(data?.selectedUsers?.length ?? 0);
            } catch (error) {
              console.error(error);
              setStep('save-error');
            }
          },
        },
      };
    }

    default:
      return {
        right: undefined,
        left: {
          type: 'cancel',
          onClick: props.onClose,
        },
      };
  }
}

async function saveData({
  props,
  data,
}: {
  props: PlannerCreateActivityModalProps;
  data?: CreateActivityData;
}) {
  const activitiesSectorId = props.activitiesSector.id!;
  const activitiesGroupId = data?.activitiesGroup?.id;
  const activityTypeId = data?.activitiesType?.id;
  const users = data?.selectedUsers;
  const formData = data?.form?.values;

  if (!activitiesGroupId || !activityTypeId || !formData || !users || users.length === 0) {
    throw new Error('Incomplete data in order to create it.', {
      cause: {activitiesGroupId, activityTypeId, formData, users},
    });
  }

  // Get Entity Id from the first user
  const entityId = users[0].data.affiliation.entityId;

  // Add Activities Group Type to Care Plan
  await EntityUserCarePlansRepository.instance.addActivitiesGroupActivityToCarePlan(
    entityId,
    users.map((x) => x.data.user.id!),
    activitiesSectorId,
    activitiesGroupId,
    {
      activityTypeId,
      configurations: DynamicFormManager.instance.remapCarePlanConfigurationValue(
        data!.activityTypeConfigurationsIndex,
        formData!
      ),
    }
  );
}

type CreateActivityData = {
  activitiesGroup?: ActivitiesGroup;
  activitiesType?: ActivityType;
  form?: ActivityForm;
  selectedUsers?: SelectedUser[];
  saving?: boolean;
  activityTypeConfigurationsIndex?: ConfigurationsIndex;
};

type SetStateStepAction = Dispatch<SetStateAction<PlannerCreateActivityStep>>;

type SetStateCreateActivityDataAction = Dispatch<SetStateAction<CreateActivityData | undefined>>;

type ActivityForm = {
  values?: Record<string, any>;
  isValid?: boolean;
};

type SelectedUser = {
  data: AffiliatedUser;
  status: 'pending' | 'saving' | 'saved' | 'error';
};
